import { useCallback, useEffect, useMemo, useState } from 'react';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { AxiosResponse } from 'axios';
import { httpGet } from '../../../../components/utils';
import { convertToQueryParams } from '../utils';
import { QueryParams } from '../../../../api';
import { PaginationConfig } from './PaginationConfig';
import { NUMBER_OF_ENTITIES_TO_FETCH } from '../consts';

export const useFetchWithCursorPagination = <TDto extends object, TEntity extends object, TFilters extends object>(
    cfg: PaginationConfig<TDto, TEntity, TFilters>
) => {
    const { filters, limit, mapResponse, sorting, url } = cfg;

    const [cursor, setCursor] = useState<string | undefined>();
    const [error, setError] = useState<string>();
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nodes, setNodes] = useState<Array<TEntity>>([]);

    const requestParams = useMemo(
        () =>
            convertToQueryParams({
                ...filters,
                limit: limit ?? NUMBER_OF_ENTITIES_TO_FETCH,
                sortDirection: sorting.direction,
                sortField: sorting.field
            }),
        [filters, limit, sorting.direction, sorting.field]
    );

    const fetchData = useCallback(
        async (requestParams: QueryParams) => {
            setError(undefined);
            setLoading(true);

            try {
                const result = (await httpGet(url, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: requestParams
                    }
                })) as unknown as AxiosResponse<{
                    nodes: Array<TDto>;
                    pageInfo: { cursor?: string; hasMore: boolean };
                }>;

                setCursor(result.data.pageInfo.cursor);
                setHasMore(result.data.pageInfo.hasMore);
                setNodes((prev) => [...prev, ...result.data.nodes.map(mapResponse)]);
            } catch (err) {
                setError('Failed to get data from server');
            } finally {
                setLoading(false);
            }
        },
        [mapResponse, url]
    );

    const fetchNextPage = useCallback(async () => {
        if (!hasMore) return;

        await fetchData(
            convertToQueryParams({
                ...requestParams,
                cursor
            })
        );
    }, [cursor, fetchData, hasMore, requestParams]);

    const refresh = useCallback(() => {
        setCursor(undefined);
        setError(undefined);
        setHasMore(false);
        setLoading(false);
        setNodes([]);

        fetchData(requestParams);
    }, [fetchData, requestParams]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        data: nodes,
        fetchNextPage,
        loading,
        refresh
    };
};
